import { useContext, useEffect, useState } from "react";
import { Card } from "primereact/card";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { DataTable } from "primereact/datatable";
import { Column, ColumnFilterElementTemplateOptions } from "primereact/column";
import { InputSwitch } from "primereact/inputswitch";

import {
  defaultPageSize,
  defaultPaginationOptions,
  emptyPaginationDetails,
  getPaginationFirst,
  getPaginationOptionsFromDataTable,
} from "../../common/api/paginationHelper";
import FilterInputText from "../../common/components/form/FilterInputText";
import { User } from "../users/User";
import UserRoleLabel from "../userRoles/UserRoleLabel";
import UserRolesDropdown from "../userRoles/UserRoleDropdown";
import GroupedTeamsDropdown from "../teams/GroupedTeamsDropdown";
import DepartmentLabel from "../departments/DepartmentLabel";
import UserForm from "./UserForm";
import NoResultsFound from "../../common/components/noResultsFound/NoResultsFound";
import { PaginationDetails } from "../../common/types/Pagination";
import { searchUsers, syncUsers, updateUser } from "../users/usersApi";
import { LayoutContext } from "../../common/components/layout/LayoutContext";
import { fixedWidthColumnStyle } from "../../common/utils/common";

export default function UsersPage() {
  const { setFullContentWidth, setStandardContentWidth } = useContext(LayoutContext);
  const [users, setUsers] = useState<User[]>([]);
  const [pagination, setPagination] = useState<PaginationDetails>(emptyPaginationDetails);
  const [first, setFirst] = useState(getPaginationFirst(pagination.pageNumber, defaultPageSize));
  const [rows, setRows] = useState(defaultPageSize);
  const [filters, setFilters] = useState<any>();
  const [selectedUser, setSelectedUser] = useState<User | undefined>(undefined);
  const [showUserDialog, setShowUserDialog] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    setFullContentWidth();
    return () => setStandardContentWidth();
  }, []);

  useEffect(() => {
    setIsLoading(true);
    doSearchUsers();
  }, [first, rows, filters]);

  const doSearchUsers = async () => {
    const response = await searchUsers({
      firstName: filters?.firstName?.value,
      lastName: filters?.lastName?.value,
      emailAddress: filters?.emailAddress?.value,
      role: filters?.role?.value,
      teamId: filters?.teamIds?.value,
      canApproveTickets: filters?.canApproveTickets?.value,
      ...getPaginationOptionsFromDataTable(first, rows),
    });
    setPagination(response.pagination);
    setUsers(response.users);
    setIsLoading(false);
  };

  const closeDialog = () => {
    setSelectedUser(undefined);
    setShowUserDialog(false);
  };

  const onEdit = (user: User) => {
    setSelectedUser(user);
    setShowUserDialog(true);
  };

  const onSubmit = async (user: User) => {
    setIsLoading(true);
    await updateUser(user);
    await doSearchUsers();
    closeDialog();
  };

  const onSync = async () => {
    setIsLoading(true);
    await syncUsers();
    doSearchUsers();
  };

  const resetPagination = () => {
    setPagination(emptyPaginationDetails);
    setFirst(getPaginationFirst(emptyPaginationDetails.pageNumber, defaultPageSize));
    setRows(defaultPageSize);
  };

  return (
    <Card
      title={
        <div className="flex align-items-center justify-content-between">
          <div className="text-2xl font-bold">Utilizatori</div>
          <Button label="Sincronizeaza" icon="pi pi-sync" onClick={() => onSync()} />
        </div>
      }
    >
      <DataTable
        pt={{
          column: {
            headerFilterClearButton: {
              className: "display-none",
            },
          },
        }}
        size="small"
        lazy
        value={users}
        paginator
        first={first}
        rows={rows}
        rowsPerPageOptions={defaultPaginationOptions}
        totalRecords={pagination.totalCount}
        filterDisplay="row"
        filters={filters}
        filterDelay={500}
        onPage={(e) => {
          setFirst(e.first);
          setRows(e.rows);
        }}
        onFilter={(e) => {
          setFilters(e.filters);
          resetPagination();
        }}
        emptyMessage={<NoResultsFound />}
        loading={isLoading}
      >
        <Column
          header="Nume"
          field="lastName"
          filter
          filterElement={(options: ColumnFilterElementTemplateOptions) => (
            <FilterInputText
              iconPosition="right"
              placeholder="Nume"
              value={options.value}
              onChange={(e) => options.filterApplyCallback(e)}
            />
          )}
          showFilterMenu={false}
        ></Column>
        <Column
          header="Prenume"
          field="firstName"
          filter
          filterElement={(options: ColumnFilterElementTemplateOptions) => (
            <FilterInputText
              iconPosition="right"
              placeholder="Prenume"
              value={options.value}
              onChange={(e) => options.filterApplyCallback(e)}
            />
          )}
          showFilterMenu={false}
        ></Column>
        <Column
          header="Email"
          field="emailAddress"
          filter
          filterElement={(options: ColumnFilterElementTemplateOptions) => (
            <FilterInputText
              iconPosition="right"
              placeholder="Email"
              value={options.value}
              onChange={(e) => options.filterApplyCallback(e)}
            />
          )}
          showFilterMenu={false}
        ></Column>
        <Column
          header="Rol"
          field="role"
          body={(rowData: User) => <UserRoleLabel value={rowData.role} />}
          filter
          filterElement={(options: ColumnFilterElementTemplateOptions) => (
            <UserRolesDropdown showClear value={options.value} onChange={(e) => options.filterApplyCallback(e)} />
          )}
          showFilterMenu={false}
        ></Column>
        <Column
          header="Grupa"
          field="teamIds"
          body={(rowData: User) => rowData.teamNames.map((teamName) => <span key={teamName}>{teamName}</span>)}
          filter
          filterElement={(options: ColumnFilterElementTemplateOptions) => (
            <GroupedTeamsDropdown showClear value={options.value} onChange={(e) => options.filterApplyCallback(e)} />
          )}
          showFilterMenu={false}
        ></Column>
        <Column
          header="Departament"
          field="departmentId"
          body={(rowData: User) => <DepartmentLabel value={rowData.departmentId} />}
        ></Column>
        <Column
          style={fixedWidthColumnStyle(140)}
          className="text-center"
          header="Manager aprobari"
          field="canApproveTickets"
          filter
          filterElement={(options: ColumnFilterElementTemplateOptions) => (
            <InputSwitch checked={options.value} onChange={(e) => options.filterApplyCallback(e.value ? true : null)} />
          )}
          showFilterMenu={false}
          body={(rowData: User) => <i className={rowData.canApproveTickets ? "pi pi-check text-green-500" : "pi pi-times text-red-500"} />}
        ></Column>
        <Column
          className="text-right"
          body={(rowData: User) => <Button size="small" icon="pi pi-pencil" rounded text onClick={() => onEdit(rowData)} />}
        ></Column>
      </DataTable>
      <Dialog className="w-almost-full md:w-8" header="Editeaza utilizator" visible={showUserDialog} onHide={() => closeDialog()}>
        <UserForm user={selectedUser} onSubmit={onSubmit} />
      </Dialog>
    </Card>
  );
}
